/**
 * Custom XML Parts for persisting KobSign metadata in the DOCX file.
 *
 * Requires WordApi 1.4+. Operations are best-effort — failures are
 * logged but don't block the user workflow.
 *
 * Metadata stored as Custom XML Part survives editing rounds and can
 * be read by the backend directly from the DOCX without Office.js.
 */

import { devLog } from '../services/devLog'

const NAMESPACE = 'http://schemas.kobsign.com/document/1.0'

interface KobSignXmlMetadata {
  counterparty?: string
  document_type?: string
  contract_value?: string
  contract_currency?: string
  contract_expires_at?: string
  department?: string
  responsible_person?: string
  owner_actor_id?: string
}

/** Check if Custom XML Parts API is available */
function isSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported('WordApi', '1.4')
  } catch {
    return false
  }
}

/** Build XML string from metadata */
function buildXml(data: KobSignXmlMetadata): string {
  const fields = Object.entries(data)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `  <${k}>${escapeXml(v!)}</${k}>`)
    .join('\n')
  return `<?xml version="1.0" encoding="UTF-8"?>\n<KobSign xmlns="${NAMESPACE}">\n${fields}\n</KobSign>`
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Write metadata as a Custom XML Part in the DOCX.
 * Replaces any existing KobSign XML part.
 */
export async function writeXmlMetadata(data: KobSignXmlMetadata): Promise<void> {
  if (!isSupported()) {
    devLog.info('XML Parts', 'Not supported (requires WordApi 1.4+)')
    return
  }

  try {
    await Word.run(async (context) => {
      const xmlParts = context.document.customXmlParts

      // Remove existing KobSign parts
      const existing = xmlParts.getByNamespace(NAMESPACE)
      existing.load('items')
      await context.sync()

      for (const part of existing.items) {
        part.delete()
      }

      // Add new part
      const xml = buildXml(data)
      xmlParts.add(xml)
      await context.sync()

      devLog.info('XML Parts', `Wrote ${Object.keys(data).length} field(s)`)
    })
  } catch (e) {
    devLog.warn('XML Parts', `Write failed: ${e}`)
  }
}

/** Read metadata from the KobSign Custom XML Part */
export async function readXmlMetadata(): Promise<KobSignXmlMetadata | null> {
  if (!isSupported()) {
    return null
  }

  try {
    return await Word.run(async (context) => {
      const xmlParts = context.document.customXmlParts
      const parts = xmlParts.getByNamespace(NAMESPACE)
      parts.load('items')
      await context.sync()

      if (parts.items.length === 0) {
        return null
      }

      const xml = parts.items[0].getXml()
      await context.sync()

      return parseXml(xml.value)
    })
  } catch (e) {
    devLog.warn('XML Parts', `Read failed: ${e}`)
    return null
  }
}

/** Parse KobSign XML into metadata object */
function parseXml(xmlString: string): KobSignXmlMetadata {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')
  const root = doc.documentElement

  const metadata: KobSignXmlMetadata = {}
  const fields = [
    'counterparty', 'document_type', 'contract_value',
    'contract_currency', 'contract_expires_at', 'department', 'responsible_person',
    'owner_actor_id',
  ] as const

  for (const field of fields) {
    const el = root.getElementsByTagNameNS(NAMESPACE, field)[0]
      ?? root.getElementsByTagName(field)[0]
    if (el?.textContent) {
      metadata[field] = el.textContent
    }
  }

  devLog.info('XML Parts', `Parsed ${Object.keys(metadata).length} field(s)`)
  return metadata
}

/** Delete all KobSign Custom XML Parts from the document */
export async function deleteXmlMetadata(): Promise<void> {
  if (!isSupported()) return

  try {
    await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(NAMESPACE)
      parts.load('items')
      await context.sync()

      for (const part of parts.items) {
        part.delete()
      }
      await context.sync()
      devLog.info('XML Parts', 'Deleted all KobSign XML parts')
    })
  } catch (e) {
    devLog.warn('XML Parts', `Delete failed: ${e}`)
  }
}

// --- Negotiation Context ---
// Separate XML namespace so negotiation metadata coexists with document metadata.

const NEGOTIATION_NAMESPACE = 'http://schemas.kobsign.com/negotiation/1.0'

export interface NegotiationContext {
  negotiation_id: number
  current_version: number
  title: string
  owner_actor_id?: string
}

function buildNegotiationXml(ctx: NegotiationContext): string {
  const lines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<KobSignNegotiation xmlns="${NEGOTIATION_NAMESPACE}">`,
    `  <negotiation_id>${ctx.negotiation_id}</negotiation_id>`,
    `  <current_version>${ctx.current_version}</current_version>`,
    `  <title>${escapeXml(ctx.title)}</title>`,
  ]
  if (ctx.owner_actor_id) {
    lines.push(`  <owner_actor_id>${escapeXml(ctx.owner_actor_id)}</owner_actor_id>`)
  }
  lines.push('</KobSignNegotiation>')
  return lines.join('\n')
}

function parseNegotiationXml(xmlString: string): NegotiationContext | null {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')
  const root = doc.documentElement

  const getId = (tag: string) => {
    const el = root.getElementsByTagNameNS(NEGOTIATION_NAMESPACE, tag)[0]
      ?? root.getElementsByTagName(tag)[0]
    return el?.textContent ?? null
  }

  const idStr = getId('negotiation_id')
  const versionStr = getId('current_version')
  const title = getId('title')
  const ownerActorId = getId('owner_actor_id')

  if (!idStr || !versionStr) return null

  return {
    negotiation_id: parseInt(idStr, 10),
    current_version: parseInt(versionStr, 10),
    title: title ?? '',
    owner_actor_id: ownerActorId ?? undefined,
  }
}

/** Write negotiation context as a Custom XML Part */
export async function writeNegotiationContext(ctx: NegotiationContext): Promise<void> {
  if (!isSupported()) {
    devLog.info('XML Parts', 'Not supported — skipping negotiation context write')
    return
  }

  try {
    await Word.run(async (context) => {
      const xmlParts = context.document.customXmlParts

      // Remove existing negotiation parts
      const existing = xmlParts.getByNamespace(NEGOTIATION_NAMESPACE)
      existing.load('items')
      await context.sync()

      for (const part of existing.items) {
        part.delete()
      }

      xmlParts.add(buildNegotiationXml(ctx))
      await context.sync()

      devLog.info('XML Parts', `Wrote negotiation context: id=${ctx.negotiation_id}, v=${ctx.current_version}`)
    })
  } catch (e) {
    devLog.warn('XML Parts', `Negotiation context write failed: ${e}`)
  }
}

/** Read negotiation context from the DOCX */
export async function readNegotiationContext(): Promise<NegotiationContext | null> {
  if (!isSupported()) return null

  try {
    return await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(NEGOTIATION_NAMESPACE)
      parts.load('items')
      await context.sync()

      if (parts.items.length === 0) return null

      const xml = parts.items[0].getXml()
      await context.sync()

      const ctx = parseNegotiationXml(xml.value)
      if (ctx) {
        devLog.info('XML Parts', `Read negotiation context: id=${ctx.negotiation_id}, v=${ctx.current_version}`)
      }
      return ctx
    })
  } catch (e) {
    devLog.warn('XML Parts', `Negotiation context read failed: ${e}`)
    return null
  }
}

/** Delete negotiation context from the DOCX */
export async function deleteNegotiationContext(): Promise<void> {
  if (!isSupported()) return

  try {
    await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(NEGOTIATION_NAMESPACE)
      parts.load('items')
      await context.sync()

      for (const part of parts.items) {
        part.delete()
      }
      await context.sync()
      devLog.info('XML Parts', 'Deleted negotiation context')
    })
  } catch (e) {
    devLog.warn('XML Parts', `Negotiation context delete failed: ${e}`)
  }
}

// --- Document Binding ---
// Binds a KobSign document (koblink_id) to this Word file so the panel
// can detect that the document already has an active signing/negotiation process.

const BINDING_NAMESPACE = 'http://schemas.kobsign.com/binding/1.0'

export interface DocumentBinding {
  koblink_id: string
  title: string
  status: string
  bound_at: string
  owner_actor_id?: string
}

function buildBindingXml(binding: DocumentBinding): string {
  const lines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<KobSignBinding xmlns="${BINDING_NAMESPACE}">`,
    `  <koblink_id>${escapeXml(binding.koblink_id)}</koblink_id>`,
    `  <title>${escapeXml(binding.title)}</title>`,
    `  <status>${escapeXml(binding.status)}</status>`,
    `  <bound_at>${escapeXml(binding.bound_at)}</bound_at>`,
  ]
  if (binding.owner_actor_id) {
    lines.push(`  <owner_actor_id>${escapeXml(binding.owner_actor_id)}</owner_actor_id>`)
  }
  lines.push('</KobSignBinding>')
  return lines.join('\n')
}

function parseBindingXml(xmlString: string): DocumentBinding | null {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')
  const root = doc.documentElement

  const get = (tag: string) => {
    const el = root.getElementsByTagNameNS(BINDING_NAMESPACE, tag)[0]
      ?? root.getElementsByTagName(tag)[0]
    return el?.textContent ?? null
  }

  const koblinkId = get('koblink_id')
  const title = get('title')
  const status = get('status')
  const boundAt = get('bound_at')

  if (!koblinkId || !status) return null

  return {
    koblink_id: koblinkId,
    title: title ?? '',
    status,
    bound_at: boundAt ?? '',
    owner_actor_id: get('owner_actor_id') ?? undefined,
  }
}

/** Write document binding as a Custom XML Part */
export async function writeDocumentBinding(binding: DocumentBinding): Promise<void> {
  if (!isSupported()) {
    devLog.info('XML Parts', 'Not supported — skipping document binding write')
    return
  }

  try {
    await Word.run(async (context) => {
      const xmlParts = context.document.customXmlParts

      const existing = xmlParts.getByNamespace(BINDING_NAMESPACE)
      existing.load('items')
      await context.sync()

      for (const part of existing.items) {
        part.delete()
      }

      xmlParts.add(buildBindingXml(binding))
      await context.sync()

      devLog.info('XML Parts', `Wrote document binding: ${binding.koblink_id} (${binding.status})`)
    })
  } catch (e) {
    devLog.warn('XML Parts', `Document binding write failed: ${e}`)
  }
}

/** Read document binding from the DOCX */
export async function readDocumentBinding(): Promise<DocumentBinding | null> {
  if (!isSupported()) return null

  try {
    return await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(BINDING_NAMESPACE)
      parts.load('items')
      await context.sync()

      if (parts.items.length === 0) return null

      const xml = parts.items[0].getXml()
      await context.sync()

      const binding = parseBindingXml(xml.value)
      if (binding) {
        devLog.info('XML Parts', `Read document binding: ${binding.koblink_id} (${binding.status})`)
      }
      return binding
    })
  } catch (e) {
    devLog.warn('XML Parts', `Document binding read failed: ${e}`)
    return null
  }
}

/** Delete document binding from the DOCX */
export async function deleteDocumentBinding(): Promise<void> {
  if (!isSupported()) return

  try {
    await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(BINDING_NAMESPACE)
      parts.load('items')
      await context.sync()

      for (const part of parts.items) {
        part.delete()
      }
      await context.sync()
      devLog.info('XML Parts', 'Deleted document binding')
    })
  } catch (e) {
    devLog.warn('XML Parts', `Document binding delete failed: ${e}`)
  }
}
